/**
 * The sub-fab as data: where the lower level is, how you get down to it, and
 * what stands on its slab.
 *
 * Split from the geometry on purpose. The house model has taught "the broken
 * tool is downstairs" since before there was a downstairs; now that there is
 * one, the claims about it - the stair is walkable, the opening is railed,
 * every house system has a skid a student can stand in front of - are exactly
 * the kind of thing this project asserts rather than assumes. Assertions need
 * numbers without a WebGL context, so the numbers live here and the meshes in
 * `subfab.ts` are built FROM them.
 *
 * WHY UNDER THE SUPPORT END. The stair drops out of the bay behind FAC-06,
 * which is where the hazard striping has always pointed and where a facilities
 * technician would actually go down. The room below spans the metrology and
 * interconnect bays - one section of a real sub-fab, not the whole footprint,
 * because one section is enough to hold all five house systems and a student
 * can learn the geography of it in a minute.
 */

import type { HouseSystem } from '../sim/house/model'
import { BALLROOM, HALL_LAYOUT, onHallChange, type HallLayout } from './hall'
import { AUTHORED_TOOL_X, TOOLS } from './layout'
import type { Collider } from './Player'

/** The lower level, metres, in the same world coordinates as the fab floor. */
export const SUBFAB = {
  minX: 2,
  maxX: 42,
  minZ: -10,
  maxZ: 11.2,
  /**
   * Fab floor slab to sub-fab slab. A sub-fab is a tall room, because two
   * tiers of rack, the trunk ducts and the beams all have to clear a
   * technician's head with a pump on a pallet jack; and 3.6 m is the most
   * a single flight of stair may climb before the code wants a landing in
   * it (3.7 m, OSHA 1910.25), which this stair has no room for.
   */
  depth: 3.6,
  /** Underside of the fab floor slab, seen from below. */
  ceilingY: -0.35,
  /**
   * The stair descends in +X: ground level at minX, the full depth at maxX.
   * Slope 3.6 m over 6 m is 31 degrees, 20 risers of 180 mm on 300 mm
   * goings - a real industrial stair.
   */
  stair: { minX: 10, maxX: 16, minZ: 9.4, maxZ: 11.0 },
} as const

/**
 * Walkable ground height at a point.
 *
 * `foot` is the player's CURRENT ground height, and it is what disambiguates
 * the two levels: over most of the sub-fab the same (x, z) has a floor at 0
 * and a slab at -depth, and which one you are standing on is a fact about
 * you, not about the point. The stair is the one place the answer is a ramp,
 * and it needs no disambiguation because it is the only surface there - the
 * fab floor has a hole where the stair is.
 */
export function groundHeightAt(x: number, z: number, foot: number): number {
  const s = SUBFAB.stair
  if (x >= s.minX && x <= s.maxX && z >= s.minZ && z <= s.maxZ) {
    return (-SUBFAB.depth * (x - s.minX)) / (s.maxX - s.minX)
  }
  if (
    foot < -SUBFAB.depth / 2 &&
    x > SUBFAB.minX &&
    x < SUBFAB.maxX &&
    z > SUBFAB.minZ &&
    z < SUBFAB.maxZ
  ) {
    return -SUBFAB.depth
  }
  return 0
}

export type SkidKind = 'pumps' | 'chiller' | 'dryer' | 'upw' | 'ahu'

export interface Skid {
  system: HouseSystem
  kind: SkidKind
  /** Centre of the skid on the sub-fab slab. */
  pos: [number, number]
  /** Footprint, metres. */
  size: [number, number]
  /** Which way the placard and beacon face: the service aisle. */
  facing: 1 | -1
}

/**
 * One skid per house system - the whole point of the room. A student who has
 * just been told "escalate to facilities" walks down here and finds the thing
 * the escalation is FOR: the pumps behind the vacuum header, the chiller
 * behind the PCW offset, the dryer behind the wet gas, the mixed beds behind
 * the rinse water, the air handlers behind the starving bays.
 */
export const SKIDS: Skid[] = [
  { system: 'vacuum', kind: 'pumps', pos: [12, -8], size: [16, 2.4], facing: -1 },
  { system: 'pcw', kind: 'chiller', pos: [5.5, 3], size: [3.6, 4.2], facing: 1 },
  { system: 'gas', kind: 'dryer', pos: [30, -7.5], size: [3.2, 2.6], facing: -1 },
  { system: 'upw', kind: 'upw', pos: [36.5, 5.5], size: [6.4, 3.0], facing: -1 },
  { system: 'hvac', kind: 'ahu', pos: [25, 8.8], size: [10, 2.8], facing: 1 },
]

/** Structural columns holding the fab floor up - visual weight, real colliders. */
export const COLUMNS: Array<[number, number]> = [
  [9, -3.5],
  [20, -3.5],
  [31, -3.5],
  [9, 3.5],
  [20, 3.5],
  [31.5, 4.2],
]

/** Where the live utility board hangs: the wall a student faces at the stair foot. */
export const UTILITY_BOARD = { x: 18, z: 11.1, facing: -1 as const }

/**
 * The overhead pipe racks - the thing a real sub-fab is mostly made of.
 *
 * Two racks, one along each column line, the length of the room: the
 * columns hold the fab floor up AND carry the racks, which is why the racks
 * sit exactly over them. Each rack has two tiers: piping below (vacuum
 * header, chilled water supply and return, dry gas, UPW supply and return,
 * in the colour code) and the exhaust trunk plus cable tray above. Every
 * tool drop branches off a rack, so a student can follow any utility from
 * its skid, along a rack, up a riser and into the tool that uses it.
 */
export const RACK = {
  /** Half-width of the strut, m. */
  halfWidth: 0.85,
  /** Upper tier (exhaust trunk, cable tray): metres below the ceiling - under the downstand beams. */
  upperDrop: 0.75,
  /** Lower tier (piping): metres below the ceiling - 2.4 m over the slab, clear of a head. */
  lowerDrop: 1.2,
  /** Trapeze hangers every this many metres. */
  hangerSpacing: 3,
} as const

export const PIPE_RACKS: Array<{ z: number; minX: number; maxX: number }> = [
  { z: -3.5, minX: SUBFAB.minX + 1, maxX: SUBFAB.maxX - 1 },
  { z: 3.5, minX: SUBFAB.minX + 1, maxX: SUBFAB.maxX - 1 },
]

/**
 * A tool's point of connection: where its utilities leave the rack and
 * rise through the slab into the mainframe standing on the floor above.
 *
 * `services` says which house systems that tool actually draws - a CMP tool
 * has no process vacuum and a metrology tool takes no rinse water - and
 * `hvac` stands for the exhaust duct, which every tool has. The placard on
 * the riser lists them, so the room teaches which tool hangs off which
 * plant, which is the fact the phone-call escalation upstairs turns on.
 */
export interface ToolDrop {
  /** The tool standing above, by asset tag - the physical label on the riser. */
  assetTag: string
  /** Where the riser bundle stands on the slab. */
  x: number
  z: number
  services: HouseSystem[]
}

/**
 * The riser bundle's footprint around (x, z): vacuum, water, gas and UPW
 * risers in a row in +x, the exhaust duct riser at the +x end.
 */
export const DROP_FOOTPRINT = { minDx: -0.6, maxDx: 0.9, halfDz: 0.45 } as const

export const TOOL_DROPS: ToolDrop[] = [
  { assetTag: 'MET-05', x: 6.0, z: -4.9, services: ['vacuum', 'pcw', 'gas', 'hvac'] },
  { assetTag: 'CMP-11', x: 18.6, z: -4.9, services: ['pcw', 'upw', 'gas', 'hvac'] },
  { assetTag: 'INSP-13', x: 33.8, z: -4.9, services: ['vacuum', 'pcw', 'gas', 'hvac'] },
  { assetTag: 'ECD-12', x: 18.6, z: 4.9, services: ['pcw', 'upw', 'gas', 'hvac'] },
  { assetTag: 'PRB-14', x: 31.9, z: 5.1, services: ['vacuum', 'pcw', 'gas', 'hvac'] },
]

/**
 * The motor control centre: the lineup of cabinets on the north wall that
 * every pump, fan and compressor in the room is fed from. It carries a
 * painted keep-clear zone in front, because the working clearance in front
 * of live switchgear is a rule, not a courtesy.
 */
export const MCC = {
  minX: 21.7,
  maxX: 26.3,
  /** Cabinet centre line, just off the wall. */
  z: SUBFAB.minZ + 0.45,
  depth: 0.55,
  /** Painted clearance in front of the cabinets, m. */
  clearance: 1.0,
} as const

/**
 * The exhaust plant at the east end: the scrubber column the rack's exhaust
 * trunk empties into, and the fan that pulls the whole room's process
 * exhaust through it and up the stack.
 */
export const EXHAUST_PLANT = { x: 39.6, z: -6.5, radius: 0.7 } as const

/**
 * The air path, which is what most of a sub-fab is for.
 *
 * The cleanroom's air comes DOWN: through the perforated raised floor, through
 * the open cells of the waffle slab overhead, into this room - the sub-fab is
 * the return plenum. The recirculation units stand down here and breathe it,
 * through the louvred intake between them, and push it up their risers to
 * the ceiling plenum and the filters. The make-up air unit is the other half:
 * outside air filtered, heated, cooled, reheated, humidified and pushed up to
 * top the loop off and hold the cleanroom at positive pressure. A student who
 * has stood under the grilles and in front of the intake has seen the loop the
 * ahuBeltSlip fault starves.
 *
 * ONE path, not two. An earlier room also had louvred return chases on the
 * perimeter walls carrying plenum air up to fab-level recirculation units -
 * a real design, but a different one, and the two together said the same air
 * went up two ways. The belt-slip fault lives in the units down here, so the
 * units stayed and the chases went; the type and the list remain for a room
 * built the other way.
 */
export const MAU = {
  minX: 32.8,
  maxX: 39.8,
  /** Casing centre line; the unit backs onto the south wall. */
  z: 9.7,
  depth: 2.2,
} as const

export interface ReturnChase {
  x: number
  z: number
  w: number
  d: number
  /** Which way its louvred face looks: into the room. */
  facing: 1 | -1
}

/** Return-air chases on the perimeter walls: none in this room - see the air path above. */
export const RETURN_CHASES: ReturnChase[] = []

/**
 * The airlock on the stair.
 *
 * The sub-fab is not a cleanroom, so the stair cannot simply be a hole in
 * the cleanroom floor: an open stairwell would put the two rooms on one air
 * volume and the pressure cascade would be a placard and nothing else. The
 * stair is enclosed instead - a vestibule on the fab floor at its head and
 * a door at its foot - and the two doors are interlocked: the second never
 * opens until the first has closed, so the stair itself is the lock, and the
 * cleanroom's air never meets the sub-fab's.
 *
 * The door colliders are the ONE piece of mutable geometry in the layout.
 * Colliders are handed to the player once, by reference, so opening a door
 * is done by moving its collider out of everyone's height range in place -
 * `setAirlockDoor` - rather than by rebuilding the list.
 */
export const AIRLOCK = {
  /** The vestibule on the fab floor, at the head of the stair, in the chase behind FAC-06. */
  vestibule: { minX: 7.5, maxX: 10.0, minZ: 9.4, maxZ: 11.0, height: 2.4 },
  /**
   * The upper door is in the vestibule's aisle-side wall, facing the chase
   * corridor a technician walks along behind the tools - the hall is bay
   * and chase, and the corridor is the only way anyone arrives here. The
   * leaf slides along the wall, westward, into a pocket beside the door.
   */
  upperDoor: { x: 8.75, z: 9.4, width: 1.6 },
  /** The lower door: past the stair foot and the guardrail over it, so the foot itself stays clear. */
  lowerDoorX: 16.9,
  /** A door opens for a person this close, and only while the other is shut. */
  approach: 1.7,
} as const

const DOOR_HEIGHTS = {
  upper: { minY: -0.5, maxY: 2.4 },
  lower: { minY: -SUBFAB.depth - 0.5, maxY: SUBFAB.ceilingY },
} as const

export type AirlockDoor = 'upper' | 'lower'

export const AIRLOCK_DOORS: Record<AirlockDoor, Collider> = {
  upper: {
    minX: AIRLOCK.upperDoor.x - AIRLOCK.upperDoor.width / 2,
    maxX: AIRLOCK.upperDoor.x + AIRLOCK.upperDoor.width / 2,
    minZ: AIRLOCK.upperDoor.z - 0.1,
    maxZ: AIRLOCK.upperDoor.z + 0.1,
    ...DOOR_HEIGHTS.upper,
  },
  lower: { minX: AIRLOCK.lowerDoorX - 0.1, maxX: AIRLOCK.lowerDoorX + 0.1, minZ: 9.4, maxZ: 11.0, ...DOOR_HEIGHTS.lower },
}

/** Open or shut a door: its collider leaves or rejoins the player's height range. */
export function setAirlockDoor(which: AirlockDoor, open: boolean): void {
  AIRLOCK_DOORS[which].maxY = open ? DOOR_HEIGHTS[which].minY - 1 : DOOR_HEIGHTS[which].maxY
}

export function airlockDoorOpen(which: AirlockDoor): boolean {
  return (AIRLOCK_DOORS[which].maxY ?? 99) < DOOR_HEIGHTS[which].minY
}

/**
 * Everything the airlock adds to the collision list: the two doors, the
 * vestibule's side walls at fab level, and the door pockets at the stair
 * foot. Exported as a list so the harness can tell them from the stair's
 * own guards.
 */
export const AIRLOCK_COLLIDERS: Collider[] = [
  AIRLOCK_DOORS.upper,
  AIRLOCK_DOORS.lower,
  // The shaft's aisle-side wall at fab level, in two runs either side of the
  // doorway; the far-side wall; and the vestibule's west end.
  { minX: AIRLOCK.vestibule.minX - 0.1, maxX: AIRLOCK_DOORS.upper.minX, minZ: 9.34, maxZ: 9.46, ...DOOR_HEIGHTS.upper },
  { minX: AIRLOCK_DOORS.upper.maxX, maxX: SUBFAB.stair.maxX + 0.5, minZ: 9.34, maxZ: 9.46, ...DOOR_HEIGHTS.upper },
  { minX: AIRLOCK.vestibule.minX - 0.1, maxX: SUBFAB.stair.maxX + 0.5, minZ: 10.94, maxZ: 11.06, ...DOOR_HEIGHTS.upper },
  { minX: AIRLOCK.vestibule.minX - 0.1, maxX: AIRLOCK.vestibule.minX + 0.1, minZ: 9.4, maxZ: 11.0, ...DOOR_HEIGHTS.upper },
  // The upper leaf's pocket runs west along the same wall line, so it
  // never crosses the corridor; the lower leaf's pocket is on the slab.
  { minX: AIRLOCK_DOORS.upper.minX - AIRLOCK.upperDoor.width - 0.05, maxX: AIRLOCK_DOORS.upper.minX, minZ: 9.34, maxZ: 9.46, ...DOOR_HEIGHTS.upper },
  { minX: AIRLOCK.lowerDoorX - 0.1, maxX: AIRLOCK.lowerDoorX + 0.1, minZ: 7.8, maxZ: 9.4, ...DOOR_HEIGHTS.lower },
]

/**
 * The crew: where the facilities technicians stand and what they are doing,
 * and the lane the one on rounds walks. Not gowned - this is not a
 * cleanroom - and the harness holds their posts standable and their lane
 * clear, the same way it holds the skids.
 */
export interface CrewPost {
  body: 'f' | 'm'
  x: number
  z: number
  /** Which way the figure faces, radians about y; the model looks down its local +z. */
  yaw: number
  clip: 'Interact' | 'Idle_Talking_Loop' | 'Idle_Loop'
  /** Ear defenders on: the post is beside running machinery. */
  loud: boolean
}

export const CREW_POSTS: CrewPost[] = [
  // At the vacuum skid's local panel, working it, in ear defenders.
  { body: 'm', x: 10.7, z: -5.75, yaw: Math.PI, clip: 'Interact', loud: true },
  // The electrician at the MCC, on the phone to the floor above.
  { body: 'f', x: 24.0, z: -8.35, yaw: Math.PI, clip: 'Idle_Talking_Loop', loud: false },
  // At the make-up air unit's filter door.
  { body: 'm', x: 35.6, z: 7.75, yaw: 0, clip: 'Idle_Loop', loud: false },
]

/**
 * Process support gear: what stands under a tool because the tool needs it,
 * as opposed to the house plant every tool shares.
 *
 * A real sub-fab is mostly this. Each tool upstairs has its own dry pump
 * down here with a foreline through the slab, and behind that pump its own
 * point-of-use abatement - a chamber's exhaust is silane, fluorine and
 * chlorine chemistry, and it is burned or scrubbed at the tool BEFORE it
 * joins the house exhaust header, never after. A room whose pumps piped
 * straight to the stack, as this one did, was quietly claiming the house
 * scrubber could take raw process gas. A CMP tool has a slurry loop;
 * a plating tool has its chemistry delivered in double-contained pipe with
 * leak detection under it; everything acid drains to a neutralisation skid
 * before it reaches the sewer; the dry gas starts at a compressor and a
 * receiver and reaches each tool through a valve manifold box; and because
 * the room is low and full of nitrogen pipe, oxygen-deficiency monitors hang
 * on the walls. Tool-tied gear names its tool, and the harness holds it
 * within reach of that tool's riser bundle.
 */
export type GearKind =
  | 'dryPump'
  | 'abatement'
  | 'supportBank'
  | 'slurry'
  | 'chemCabinet'
  | 'awn'
  | 'receiver'
  | 'vacReceiver'
  | 'vmb'
  | 'eyewash'
  | 'drums'

export interface SupportGear {
  kind: GearKind
  x: number
  z: number
  w: number
  d: number
  /** Which way its front faces, the skid convention: +1 faces -z. */
  facing: 1 | -1
  /** The tool upstairs this serves, when it serves one. */
  assetTag?: string
  /** The stencil on its front - a physical label. */
  label: string
}

export const SUPPORT_GEAR: SupportGear[] = [
  { kind: 'dryPump', x: 7.9, z: -4.9, w: 1.0, d: 0.8, facing: -1, assetTag: 'MET-05', label: 'DRY PUMP · MET-05' },
  { kind: 'dryPump', x: 35.7, z: -4.9, w: 1.0, d: 0.8, facing: -1, assetTag: 'INSP-13', label: 'DRY PUMP · INSP-13' },
  { kind: 'dryPump', x: 30.0, z: 5.1, w: 1.0, d: 0.8, facing: 1, assetTag: 'PRB-14', label: 'DRY PUMP · PRB-14' },
  // One abatement unit per pump, on the pump's outlet - see the note above.
  // It stands ALONGSIDE its pump rather than between the pump and the riser,
  // because there is no room between them: the pair is one installation, and
  // the treated outlet doubles back overhead to the riser the way it does in
  // a real bay, where the floor space is where the plant fits and the duct
  // goes wherever it must.
  { kind: 'abatement', x: 9.6, z: -6.0, w: 0.9, d: 0.9, facing: -1, assetTag: 'MET-05', label: 'POU ABATEMENT · MET-05' },
  { kind: 'abatement', x: 37.4, z: -6.0, w: 0.9, d: 0.9, facing: -1, assetTag: 'INSP-13', label: 'POU ABATEMENT · INSP-13' },
  { kind: 'abatement', x: 28.3, z: 6.2, w: 0.9, d: 0.9, facing: 1, assetTag: 'PRB-14', label: 'POU ABATEMENT · PRB-14' },
  { kind: 'slurry', x: 16.5, z: -5.0, w: 1.2, d: 0.8, facing: -1, assetTag: 'CMP-11', label: 'CMP SLURRY' },
  { kind: 'chemCabinet', x: 16.5, z: 5.0, w: 1.2, d: 0.8, facing: 1, assetTag: 'ECD-12', label: 'PLATING CHEMISTRY' },
  { kind: 'awn', x: 35.4, z: -8.4, w: 4.0, d: 2.0, facing: -1, label: 'ACID WASTE NEUTRALISATION' },
  { kind: 'receiver', x: 32.6, z: -8.6, w: 1.0, d: 1.0, facing: -1, label: 'CDA RECEIVER' },
  // The process-vacuum receiver at the end of the pump header: the reservoir
  // that lets a bank of pumps hold a steady header while chambers come and go.
  { kind: 'vacReceiver', x: 20.85, z: -8.0, w: 1.1, d: 2.0, facing: -1, label: 'PROCESS VACUUM RECEIVER' },
  { kind: 'vmb', x: 27.3, z: -9.85, w: 0.9, d: 0.35, facing: -1, label: 'N2 · CDA VMB' },
  // Safety stations. ANSI Z358.1 wants an eyewash and a drench shower within
  // ten seconds' unobstructed travel of anywhere corrosives are handled, so
  // there is one beside each chemistry cabinet and one at the waste skid.
  // They are the reason a student can tell which parts of this room are the
  // dangerous ones without reading a single placard.
  { kind: 'eyewash', x: 14.6, z: -5.0, w: 0.6, d: 0.6, facing: -1, label: 'EYEWASH · SHOWER' },
  { kind: 'eyewash', x: 14.6, z: 5.0, w: 0.6, d: 0.6, facing: 1, label: 'EYEWASH · SHOWER' },
  { kind: 'eyewash', x: 38.8, z: -5.0, w: 0.6, d: 0.6, facing: -1, label: 'EYEWASH · SHOWER' },
  // The drum store the two chemistry cabinets are charged from, on a bunded
  // pallet with the room's spill kit beside it. Slurry and plating chemistry
  // arrive down here by the drum and go up to the day tanks from here, which
  // is why it stands between the two cabinets and a step from their shower.
  { kind: 'drums', x: 13.2, z: 6.6, w: 1.6, d: 1.2, facing: 1, label: 'CHEMICAL DRUM STORE' },
]

/**
 * Fire protection: the wet sprinkler grid under the slab.
 *
 * NFPA 318 sprinklers a fab throughout, sub-fab included, and a room this
 * full of pump oil, solvent and cable would be protected before it was
 * occupied. It is drawn because it is the layer a real ceiling has and this
 * one did not: branch lines the length of the room with upright heads every
 * few metres, fed from a cross main and a riser with its control valve at
 * eye height, where a valve that has to be found in a hurry belongs.
 *
 * Routed BELOW the downstand beams and BESIDE the pipe racks: sprinkler pipe
 * goes in last on a real job and dodges everything already there, and a head
 * with a beam over it is a head that wets the beam.
 */
export const SPRINKLERS = {
  /** Branch-line centres in z, chosen to miss the racks and beams at ±3.5. */
  branchZ: [-8.4, -6.0, -1.6, 1.6, 6.0, 8.4],
  /** Underside of the branch pipe, m - under the 0.35 m beams, over a head. */
  y: SUBFAB.ceilingY - 0.52,
  /** The cross main that feeds them, and the riser that feeds it. */
  mainX: 3.4,
  riser: { x: 2.75, z: -1.6 },
  /** Upright heads this far apart along a branch, m. */
  headSpacing: 3.2,
} as const

/** Oxygen-deficiency monitors: on the walls, at head height, no footprint. */
export const O2_MONITORS: Array<{ x: number; z: number; rot: number }> = [
  { x: SUBFAB.minX + 0.14, z: -2.5, rot: Math.PI / 2 },
  { x: SUBFAB.maxX - 0.14, z: 4.0, rot: -Math.PI / 2 },
  { x: 20.5, z: SUBFAB.minZ + 0.14, rot: 0 },
]

/** The technician on rounds: the painted walkway, end to end, on the side the chiller does not crowd. */
export const CREW_WALK = { z: -1.2, minX: 4.5, maxX: 39.5, speed: 1.0 } as const

/**
 * Colliders for the lower level and the stairwell.
 *
 * The stair cheeks and the far-end guardrail span from the sub-fab slab up
 * past the fab floor, so the opening cannot be entered from the side at
 * either level; the far-end rail stops at the fab floor's handrail height
 * and STARTS above head height of the stair's foot, so the one path through
 * it is the stair itself. Skid and column colliders carry a y-range confined
 * to the lower level, so the tools standing on the floor above them never
 * fight them.
 */
export function subfabColliders(): Collider[] {
  const s = SUBFAB.stair
  const low = { minY: -SUBFAB.depth - 0.5, maxY: SUBFAB.ceilingY }
  const out: Collider[] = []

  // Stair cheek walls, both sides, full height: slab to above the fab floor.
  for (const z of [s.minZ, s.maxZ]) {
    out.push({
      minX: s.minX - 0.1,
      maxX: s.maxX + 0.3,
      minZ: z - 0.06,
      maxZ: z + 0.06,
      minY: -SUBFAB.depth - 0.5,
      maxY: 1.2,
    })
  }
  // Far-end guardrail: blocks the fab floor, passes the stair foot beneath it.
  out.push({
    minX: s.maxX + 0.15,
    maxX: s.maxX + 0.3,
    minZ: s.minZ,
    maxZ: s.maxZ,
    minY: -0.6,
    maxY: 1.2,
  })

  for (const skid of SKIDS) {
    const [x, z] = skid.pos
    const [w, d] = skid.size
    out.push({
      minX: x - w / 2 - 0.25,
      maxX: x + w / 2 + 0.25,
      minZ: z - d / 2 - 0.25,
      maxZ: z + d / 2 + 0.25,
      ...low,
    })
  }
  for (const [x, z] of COLUMNS) {
    out.push({ minX: x - 0.35, maxX: x + 0.35, minZ: z - 0.35, maxZ: z + 0.35, ...low })
  }
  // Riser bundles stand on the slab like anything else does.
  for (const d of TOOL_DROPS) {
    out.push({
      minX: d.x + DROP_FOOTPRINT.minDx,
      maxX: d.x + DROP_FOOTPRINT.maxDx,
      minZ: d.z - DROP_FOOTPRINT.halfDz,
      maxZ: d.z + DROP_FOOTPRINT.halfDz,
      ...low,
    })
  }
  out.push({
    minX: MCC.minX,
    maxX: MCC.maxX,
    minZ: MCC.z - MCC.depth / 2,
    maxZ: MCC.z + MCC.depth / 2 + 0.1,
    ...low,
  })
  out.push({ minX: MAU.minX, maxX: MAU.maxX, minZ: MAU.z - MAU.depth / 2, maxZ: MAU.z + MAU.depth / 2, ...low })
  // The outside-air intake duct crosses head height between the wall and the unit.
  out.push({ minX: MAU.maxX, maxX: SUBFAB.maxX, minZ: MAU.z - 0.55, maxZ: MAU.z + 0.55, ...low })
  for (const c of RETURN_CHASES) {
    out.push({ minX: c.x - c.w / 2, maxX: c.x + c.w / 2, minZ: c.z - c.d / 2, maxZ: c.z + c.d / 2, ...low })
  }
  out.push(...AIRLOCK_COLLIDERS)
  for (const gr of SUPPORT_GEAR) {
    out.push({ minX: gr.x - gr.w / 2 - 0.15, maxX: gr.x + gr.w / 2 + 0.15, minZ: gr.z - gr.d / 2 - 0.15, maxZ: gr.z + gr.d / 2 + 0.15, ...low })
  }
  // The sprinkler riser, floor to slab against the west wall: a standpipe a
  // student can walk into is a standpipe that has to stop them.
  out.push({
    minX: SPRINKLERS.riser.x - 0.18,
    maxX: SPRINKLERS.riser.x + 0.18,
    minZ: SPRINKLERS.riser.z - 0.18,
    maxZ: SPRINKLERS.riser.z + 0.18,
    ...low,
  })
  out.push({
    minX: EXHAUST_PLANT.x - EXHAUST_PLANT.radius - 0.2,
    maxX: EXHAUST_PLANT.x + EXHAUST_PLANT.radius + 0.9,
    minZ: EXHAUST_PLANT.z - EXHAUST_PLANT.radius - 0.2,
    maxZ: EXHAUST_PLANT.z + EXHAUST_PLANT.radius + 0.2,
    ...low,
  })
  return out
}

// ------------------------------------------------ Fab B's support banks
//
// WHERE THE CHASE'S EQUIPMENT WENT. Fab A stands a support bank - the RF
// generator, the chiller, the controller rack - in the chase behind every
// tool. A ballroom has no chase, and its support lives down here, under
// the tool it serves, fed up through the slab: that is most of what makes
// a 300 mm sub-fab dense and its clean floor empty. So in Fab B every bay
// tool standing over this room gets its bank on the slab beneath it.
//
// PLACED, NOT WRITTEN, because this room is hand-laid and full: skids,
// columns, riser bundles, the gear above, the switchgear, the plant, the
// stair and the airlock all have footprints, the painted walkway down the
// middle has to stay clear, and the harness that checks all that runs
// under the default layout and never sees these. So each bank searches
// for a spot near its tool's x, on its tool's side, off the walkway, clear
// of everything already placed and of the banks placed before it, with
// the square metre in front of it clear too - the same rules the harness
// holds the authored gear to. A tool that finds no spot gets no bank,
// which is better than a bank in a doorway.

interface Rect {
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

const BANK = { w: 2.4, narrow: 1.4, d: 0.78, clear: 0.45, stand: 0.9 } as const

function ballroomSupportBanks(): SupportGear[] {
  const pad = (r: Rect, by: number): Rect => ({ minX: r.minX - by, maxX: r.maxX + by, minZ: r.minZ - by, maxZ: r.maxZ + by })
  const around = (x: number, z: number, w: number, d: number): Rect => ({ minX: x - w / 2, maxX: x + w / 2, minZ: z - d / 2, maxZ: z + d / 2 })
  const hits = (a: Rect, b: Rect): boolean => a.minX < b.maxX && a.maxX > b.minX && a.minZ < b.maxZ && a.maxZ > b.minZ
  const taken: Rect[] = [
    ...SKIDS.map((s) => around(s.pos[0], s.pos[1], s.size[0], s.size[1])),
    ...COLUMNS.map(([x, z]) => around(x, z, 0.6, 0.6)),
    ...SUPPORT_GEAR.map((g) => around(g.x, g.z, g.w, g.d)),
    ...TOOL_DROPS.map((d) => ({ minX: d.x + DROP_FOOTPRINT.minDx, maxX: d.x + DROP_FOOTPRINT.maxDx, minZ: d.z - DROP_FOOTPRINT.halfDz, maxZ: d.z + DROP_FOOTPRINT.halfDz })),
    { minX: MCC.minX, maxX: MCC.maxX, minZ: MCC.z - MCC.depth / 2, maxZ: MCC.z + MCC.depth / 2 + MCC.clearance },
    around(EXHAUST_PLANT.x, EXHAUST_PLANT.z, EXHAUST_PLANT.radius * 2 + 0.4, EXHAUST_PLANT.radius * 2 + 0.4),
    { minX: MAU.minX, maxX: MAU.maxX, minZ: MAU.z - MAU.depth / 2, maxZ: MAU.z + MAU.depth / 2 },
    { minX: SUBFAB.stair.minX - 0.5, maxX: SUBFAB.stair.maxX + 1.5, minZ: 7.6, maxZ: SUBFAB.maxZ },
    { minX: AIRLOCK.vestibule.minX - 0.5, maxX: AIRLOCK.vestibule.maxX + 0.5, minZ: AIRLOCK.vestibule.minZ - 0.5, maxZ: AIRLOCK.vestibule.maxZ },
  ]
  const out: SupportGear[] = []
  for (const tool of TOOLS) {
    const [tx, , tz] = tool.position
    if (tz === 0 || tx < SUBFAB.minX + 1.6 || tx > SUBFAB.maxX - 1.6) continue
    const side = Math.sign(tz) as 1 | -1
    // Faces the walkway down the middle: the skid convention, +1 faces -z.
    const facing: 1 | -1 = side > 0 ? 1 : -1
    let placed: Rect | null = null
    // The full cabinet close to the tool first; failing that, the narrow
    // one (the rack alone, the chiller elsewhere) further along and further
    // out - a bank stood two bays down is still this tool's bank, and the
    // stencil says so.
    search: for (const [bw, az, k] of (function* () {
      for (const az of [5.6, 6.5, 7.4]) for (const k of [0, 1, -1, 2, -2, 3, -3, 4, -4, 5, -5, 6, -6]) yield [BANK.w, az, k] as const
      for (const az of [5.6, 6.5, 7.4, 8.3]) for (const k of [0, 1, -1, 2, -2, 3, -3, 4, -4, 5, -5, 6, -6, 7, -7, 8, -8, 9, -9]) yield [BANK.narrow, az, k] as const
    })()) {
      {
        const x = tx + k * 0.6
        const z = side * az
        const rect = around(x, z, bw, BANK.d)
        // In the room, and off the painted walkway.
        if (rect.minX < SUBFAB.minX + 0.8 || rect.maxX > SUBFAB.maxX - 0.8) continue
        if (rect.minZ < SUBFAB.minZ + 0.8 || rect.maxZ > SUBFAB.maxZ - 0.8) continue
        if (Math.min(Math.abs(rect.minZ), Math.abs(rect.maxZ)) < 2.0) continue
        // Clear of everything, and standable in front.
        const stand: Rect = side > 0 ? { ...rect, minZ: rect.minZ - BANK.stand } : { ...rect, maxZ: rect.maxZ + BANK.stand }
        if (taken.some((t) => hits(pad(rect, BANK.clear), t) || hits(stand, t))) continue
        placed = rect
        out.push({ kind: 'supportBank', x, z, w: bw, d: BANK.d, facing, assetTag: tool.assetTag, label: `SUPPORT · ${tool.assetTag}` })
        break search
      }
    }
    if (placed) taken.push(placed)
  }
  return out
}

/**
 * THE RISERS AND PUMPS FOLLOW THEIR TOOLS. Every riser bundle and every
 * piece of tool-tied gear above was laid by hand under the tool it names,
 * where that tool stands in Fab A. Fab B packs the row (layout.ts
 * `packRow`), so the same tools stand at other x - and a bundle stencilled
 * MET-05 five metres from MET-05 is the room contradicting its own
 * labels. Each follows its tool by exactly the distance the tool moved,
 * on the same side and at the same depth, so the pipework drawn from the
 * data - the branches off the rack, the forelines, the abatement runs -
 * lands where it always did relative to the tool.
 *
 * Before the banks are placed, so the banks' search sees the room as it
 * will be.
 */
const AUTHORED_DROPS = [...TOOL_DROPS]
const AUTHORED_DROP_X = TOOL_DROPS.map((d) => d.x)
const AUTHORED_DROP_Z = TOOL_DROPS.map((d) => d.z)
const AUTHORED_GEAR = [...SUPPORT_GEAR]
const AUTHORED_GEAR_X = SUPPORT_GEAR.map((g) => g.x)
const AUTHORED_GEAR_Z = SUPPORT_GEAR.map((g) => g.z)
const AUTHORED_GEAR_FACING = SUPPORT_GEAR.map((g) => g.facing)

/**
 * Lay the room out for a hall - and again for the other one, since the
 * switch is live (hall.ts) and this file's decisions have to be made
 * again: back to the authored positions, the banks out, then the ballroom's
 * shifts and banks if that is the hall. Registered after layout.ts's own
 * listener, which is what packs the row this reads.
 */
function applySubfabLayout(layout: HallLayout): void {
  // Membership as well as position, because a floor somebody built can have
  // left a tool out and there is nothing for its riser to stand under.
  TOOL_DROPS.length = 0
  TOOL_DROPS.push(...AUTHORED_DROPS)
  TOOL_DROPS.forEach((d, i) => {
    d.x = AUTHORED_DROP_X[i]
    d.z = AUTHORED_DROP_Z[i]
  })
  SUPPORT_GEAR.length = 0
  SUPPORT_GEAR.push(...AUTHORED_GEAR)
  SUPPORT_GEAR.forEach((g, i) => {
    g.x = AUTHORED_GEAR_X[i]
    g.z = AUTHORED_GEAR_Z[i]
    g.facing = AUTHORED_GEAR_FACING[i]
  })
  if (layout === 'bayChase') return

  // A RISER UNDER NOTHING IS A LABEL THAT LIES. Gear stencilled with a tool
  // that is not on this floor comes out of the room; gear with no tag on it
  // is the room's own plant and stays whatever the floor above is.
  const onFloor = (tag?: string): boolean => !tag || TOOLS.some((t) => t.assetTag === tag)
  for (let i = TOOL_DROPS.length - 1; i >= 0; i--) {
    if (!onFloor(TOOL_DROPS[i].assetTag)) TOOL_DROPS.splice(i, 1)
  }
  for (let i = SUPPORT_GEAR.length - 1; i >= 0; i--) {
    if (!onFloor(SUPPORT_GEAR[i].assetTag)) SUPPORT_GEAR.splice(i, 1)
  }

  /**
   * Follow the tool upstairs: the same distance along, and - for a floor
   * that put the tool on the other side of the aisle, which only an
   * authored plan does - the same distance across, mirrored, facing the
   * walkway from the other side.
   */
  const follow = (g: { x: number; z: number; facing?: 1 | -1; assetTag?: string }): void => {
    if (!g.assetTag) return
    const tool = TOOLS.find((t) => t.assetTag === g.assetTag)
    const authored = AUTHORED_TOOL_X.get(g.assetTag)
    if (!tool || authored === undefined) return
    g.x += tool.position[0] - authored
    if (Math.sign(tool.position[2]) !== Math.sign(g.z)) {
      g.z = -g.z
      if (g.facing) g.facing = (-g.facing) as 1 | -1
    }
  }
  for (const d of TOOL_DROPS) follow(d)
  for (const g of SUPPORT_GEAR) follow(g)
  // AND A RISER THAT FOLLOWED ITS TOOL OUT OF THE ROOM COMES OUT TOO. The
  // sub-fab is one section of the building (SUBFAB.minX..maxX); a floor
  // somebody built can stand MET-05 forty metres from where its riser was
  // laid, and a bundle built under the fab slab outside the room would be
  // geometry nobody can reach with a placard nobody can read.
  const inRoom = (x: number): boolean => x >= SUBFAB.minX + 1.6 && x <= SUBFAB.maxX - 1.6
  for (let i = TOOL_DROPS.length - 1; i >= 0; i--) {
    if (!inRoom(TOOL_DROPS[i].x)) TOOL_DROPS.splice(i, 1)
  }
  for (let i = SUPPORT_GEAR.length - 1; i >= 0; i--) {
    if (SUPPORT_GEAR[i].assetTag && !inRoom(SUPPORT_GEAR[i].x)) SUPPORT_GEAR.splice(i, 1)
  }
  if (BALLROOM) SUPPORT_GEAR.push(...ballroomSupportBanks())
}
applySubfabLayout(HALL_LAYOUT)
onHallChange(applySubfabLayout)
